import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import btoa from "btoa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DatePicker from "react-datepicker";
import { useAppContext } from "../../../AppContext";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import "./style.scss";
import Loader from "../../settings/loader";
import {
  MENU_PERIOD_PRESETS,
  getDefaultMenuPeriodDays,
  getLastDaysRange,
  isMenuEngineeringLocation,
} from "./datePresets";
import { data } from "react-router-dom";

function Filter({ location, setFilter, hideMisc = false }) {
  const { user, chimeraserv, preservedAuditData, filterValue } =
    useAppContext();

  const props = { user, chimeraserv, location, setFilter };
  const isAdmin = user?.data?.type === "admin";
  const usesPeriodPresets = isMenuEngineeringLocation(location);
  const showDateFilter = !location.endsWith("/menu-order");
  const defaultPeriodDays = getDefaultMenuPeriodDays(location);

  // Calculate date limits - 1 year ago from today
  const getDateLimits = () => {
    if (isAdmin) {
      return {
        minDate: null,
        maxDate: null,
      };
    }
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    oneYearAgo.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);

    return {
      minDate: oneYearAgo,
      maxDate: tomorrow,
    };
  };

  const [filters, setFilters] = useState(() => {
    const date = new Date();
    const today = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0
    );
    today.setHours(0, 0, 0, 0);
    const lastDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0
    );
    lastDay.setHours(23, 59, 59, 59);

    return {
      groups: { orig: [], list: [], state: false },
      brands: { orig: [], list: [], state: false },
      payType: { orig: [], list: [], state: false },
      revCent: { orig: [], list: [], availableList: [], state: false },
      chains: { orig: [], list: [], state: false },
      date: {
        list: [
          { name: "Year", value: 0, status: false },
          { name: "Monthly", value: 1, status: false },
          { name: "Weekly", value: 2, status: false },
          { name: "Daily", value: 3, status: false },
        ],
        start: { long: today, short: parseInt(today.getTime() / 1000) },
        end: { long: lastDay, short: parseInt(lastDay.getTime() / 1000) },
        ...(isMenuEngineeringLocation(location) && {
          presetDays: defaultPeriodDays,
          ...getLastDaysRange(defaultPeriodDays),
        }),
      },
      misc: {
        list: [
          { name: "Payment Type", value: "payType", status: false },
          { name: "Revenue Center", value: "revCent", status: false },
        ],
        value: "",
      },
      state: false,
      load: false,
    };
  });
  const [reSize, setReSize] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dateError, setDateError] = useState("");
  const [groupData, setGroupData] = useState(null); // Store group data

  // Get current date limits
  const dateLimits = getDateLimits();

  // Function to get available revenue centers based on selected hierarchy using group data

  const getAvailableRevCentersFromGroup = useCallback(() => {
    if (!groupData) return filters.revCent.orig;

    let availableRevCenters = [];

    // Get selected items
    const selectedGroups = filters.groups.list.filter((group) => group.status);
    const selectedBrands = filters.brands.list.filter((brand) => brand.status);
    const selectedChains = filters.chains.list.filter((chain) => chain.status);

    if (selectedChains.length > 0) {
      // Get revenue centers from selected chains
      selectedChains.forEach((selectedChain) => {
        // Look through all groups to find the chain
        groupData.forEach((group) => {
          if (group.brands && Array.isArray(group.brands)) {
            group.brands.forEach((brand) => {
              if (brand.chains && Array.isArray(brand.chains)) {
                const chain = brand.chains.find(
                  (c) => c.chaKey === selectedChain.chaKey
                );
                if (chain && chain.revCents) {
                  availableRevCenters = [
                    ...availableRevCenters,
                    ...chain.revCents.filter((rc) => rc.status === 1),
                  ];
                }
              }
            });
          }
        });
      });
    } else if (selectedBrands.length > 0) {
      // Get revenue centers from all chains under selected brands
      selectedBrands.forEach((selectedBrand) => {
        // Look through all groups to find the brand
        groupData.forEach((group) => {
          if (group.brands && Array.isArray(group.brands)) {
            const brand = group.brands.find(
              (b) => b.braKey === selectedBrand.braKey
            );
            if (brand && brand.chains) {
              brand.chains.forEach((chain) => {
                if (chain.revCents) {
                  availableRevCenters = [
                    ...availableRevCenters,
                    ...chain.revCents.filter((rc) => rc.status === 1),
                  ];
                }
              });
            }
          }
        });
      });
    } else if (selectedGroups.length > 0) {
      // When groups are selected, get revenue centers from those specific groups

      selectedGroups.forEach((selectedGroup) => {
        // Find the specific group in groupData
        const groupInData = groupData.find(
          (g) => g.grpKey === selectedGroup.grpKey
        );

        if (groupInData) {
          if (groupInData.brands && Array.isArray(groupInData.brands)) {
            groupInData.brands.forEach((brand) => {
              if (brand.chains && Array.isArray(brand.chains)) {
                brand.chains.forEach((chain) => {
                  if (chain.revCents && Array.isArray(chain.revCents)) {
                    const activeRevCents = chain.revCents.filter(
                      (rc) => rc.status === 1
                    );
                    availableRevCenters = [
                      ...availableRevCenters,
                      ...activeRevCents,
                    ];
                  }
                });
              }
            });
          }
        } else {
        }
      });
    } else {
      // No specific selection - use all revenue centers from /revcent endpoint
      return filters.revCent.orig;
    }

    // Remove duplicates based on id and convert to expected format
    // const uniqueRevCenters = availableRevCenters.filter(
    //   (revCenter, index, self) =>
    //     index === self.findIndex((rc) => rc.id === revCenter.id)
    // );
    const uniqueRevCentersValidpair = availableRevCenters.filter(
      (revCenter, index, self) => {
        const compositeKey = `${revCenter.id}_${revCenter.name}`;
        return (
          index ===
          self.findIndex((rc) => `${rc.id}_${rc.name}` === compositeKey)
        );
      }
    );
    const uniqueRevCenters = uniqueRevCentersValidpair.filter(
      (revCenter, index, self) =>
        index === self.findIndex((rc) => rc.name === revCenter.name)
    );

    // Convert to the format expected by the filter (matching /revcent endpoint format)
    const formattedRevCenters = uniqueRevCenters.map((rc) => ({
      id: rc.id,
      name: rc.name,
      status: false, // Reset selection status
    }));

    return formattedRevCenters;
  }, [
    groupData,
    filters.groups.list,
    filters.brands.list,
    filters.chains.list,
    filters.revCent.orig,
    filters.brands.orig,
  ]);

  // Update available revenue centers when hierarchy changes
  useEffect(() => {
    if (filters.load && (filters.revCent.orig.length > 0 || groupData)) {
      const newAvailableRevCenters = getAvailableRevCentersFromGroup();

      // Preserve existing selections if they're still available
      const updatedRevCenters = newAvailableRevCenters.map((revCenter) => {
        const existingSelection = filters.revCent.list.find(
          (existing) => existing.id === revCenter.id && existing.status
        );
        return existingSelection ? { ...revCenter, status: true } : revCenter;
      });

      setFilters((prev) => ({
        ...prev,
        revCent: {
          ...prev.revCent,
          availableList: updatedRevCenters,
          list: updatedRevCenters,
        },
      }));
    }
  }, [
    filters.groups.list,
    filters.brands.list,
    filters.chains.list,
    filters.load,
    getAvailableRevCentersFromGroup,
  ]);

  // Function to validate date range
  const validateDateRange = (startDate, endDate) => {
    const { minDate, maxDate } = dateLimits;

    if (!isAdmin && startDate < minDate) {
      return `Start date cannot be earlier than ${minDate.toDateString()}`;
    }
    // if (endDate > maxDate) {
    //   return `End date cannot be later than ${maxDate.toDateString()}`;
    // }
    if (startDate > endDate) {
      return "Start date cannot be later than end date";
    }
    return "";
  };

  // Function to restore filter selections from preserved data
  const restoreFilterSelections = useCallback(
    (filterData, preservedFilterData) => {
      if (!preservedFilterData) return filterData;

      const restored = { ...filterData };

      // Restore groups
      if (preservedFilterData.groups && preservedFilterData.groups.length > 0) {
        restored.groups.list = restored.groups.list.map((group) => ({
          ...group,
          status: preservedFilterData.groups.some(
            (pg) => pg.grpKey === group.grpKey
          ),
        }));
      }

      // Restore brands
      if (preservedFilterData.brands && preservedFilterData.brands.length > 0) {
        restored.brands.list = restored.brands.list.map((brand) => ({
          ...brand,
          status: preservedFilterData.brands.some(
            (pb) => pb.braKey === brand.braKey
          ),
        }));
      }

      // Restore chains
      if (preservedFilterData.chains && preservedFilterData.chains.length > 0) {
        restored.chains.list = restored.chains.list.map((chain) => ({
          ...chain,
          status: preservedFilterData.chains.some(
            (pc) => pc.chaKey === chain.chaKey
          ),
        }));
      }

      // Restore revenue centers
      if (
        preservedFilterData.revCent &&
        preservedFilterData.revCent.length > 0
      ) {
        restored.revCent.list = restored.revCent.list.map((rev) => ({
          ...rev,
          status: preservedFilterData.revCent.some((pr) => pr.id === rev.id),
        }));
        restored.revCent.availableList = restored.revCent.list;
      }

      // Restore payment types
      if (
        preservedFilterData.payType &&
        preservedFilterData.payType.length > 0
      ) {
        restored.payType.list = restored.payType.list.map((pay) => ({
          ...pay,
          status: preservedFilterData.payType.some((pp) => pp.id === pay.id),
        }));
      }

      // Restore dates with validation
      if (preservedFilterData.date) {
        if (preservedFilterData.date.start) {
          const preservedStartDate = new Date(
            preservedFilterData.date.start * 1000
          );
          // Only restore if within allowed range
          if (isAdmin || preservedStartDate >= dateLimits.minDate) {
            restored.date.start = {
              long: preservedStartDate,
              short: preservedFilterData.date.start,
            };
          }
        }
        if (preservedFilterData.date.end) {
          const preservedEndDate = new Date(
            preservedFilterData.date.end * 1000
          );
          // Only restore if within allowed range
          if (isAdmin || preservedEndDate <= dateLimits.maxDate) {
            restored.date.end = {
              long: preservedEndDate,
              short: preservedFilterData.date.end,
            };
          }
        }
      }

      return restored;
    },
    [dateLimits]
  );

  // Fetch filter data
  useEffect(() => {
    if (dataLoaded) return;

    const headers = {
      Authorization: user.data.usrKey,
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const fetchData = async () => {
      try {
        const [
          payTypeRes,
          revCentRes,
          groupRes,
          brandRes,
          chainRes,
          groupDataRes,
        ] = await Promise.all([
          axios.get(props.chimeraserv.urlGet + "/paytype", { headers }),
          axios.get(props.chimeraserv.urlGet + "/filter/revcent", { headers }),
          axios.get(props.chimeraserv.urlGet + "/group", { headers }),
          axios.get(props.chimeraserv.urlGet + "/brand", { headers }),
          axios.get(props.chimeraserv.urlGet + "/chain", { headers }),
          axios.get(props.chimeraserv.urlGet + "/group", { headers }), // Fetch group data for revenue centers
        ]);

        const payTypeList = (payTypeRes.data || []).map((item) => ({
          ...item,
          status: false,
        }));
        const revCentList = (revCentRes.data || [])
          .map((item) => ({
            ...item,
            status: false,
          }))
          .filter(
            (item, index, self) =>
              index === self.findIndex((rc) => rc.name === item.name)
          );
        // const revCentList = (revCentRes.data || []).map((item) => ({
        //   ...item,
        //   status: false,
        // }));
        const groupList = (groupRes.data || []).map((item) => ({
          ...item,
          status: false,
        }));
        const brandList = (brandRes.data || []).map((item) => ({
          ...item,
          status: false,
        }));
        const chainList = (chainRes.data || []).map((chain) => {
          let name = chain.name;
          const brand = brandList.find((b) => chain.braKey === b.braKey);
          if (brand) {
            name = `${brand.name
              .split(" ")
              .map((b) => (!b[0] ? "" : b[0].toUpperCase()))
              .join("")} - ${chain.name}`;
          }
          return { ...chain, name, status: false };
        });

        // Store the full group data array for revenue center filtering

        // Store the entire array - don't take just the first element
        const groupDataWithStructure = Array.isArray(groupDataRes.data)
          ? groupDataRes.data
          : [groupDataRes.data]; // Ensure it's always an array

        setGroupData(groupDataWithStructure);

        let newFilters = {
          ...filters,
          payType: { orig: payTypeList, list: payTypeList, state: true },
          revCent: {
            orig: revCentList,
            list: revCentList,
            availableList: revCentList,
            state: true,
          },
          groups: { orig: groupList, list: groupList, state: true },
          brands: { orig: brandList, list: brandList, state: true },
          chains: { orig: chainList, list: chainList, state: true },
        };

        // Restore selections if preserved data exists
        if (
          filterValue &&
          (location === "/app/sales/session" ||
            location === "/app/sales/checks" ||
            location === "/app/audit/logs" ||
            location === "/app/audit/deletion" ||
            location === "/app/audit/voids" ||
            location === "/app/audit/discount")
        ) {
          newFilters = restoreFilterSelections(newFilters, filterValue.data);
        }
        setFilters(newFilters);
        setDataLoaded(true);
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    };

    fetchData();
  }, [
    dataLoaded,
    user.data.usrKey,
    props.chimeraserv.urlGet,
    filterValue,
    restoreFilterSelections,
    filters,
    location,
  ]);
  // Enable filter when all loaded
  useEffect(() => {
    if (
      !filters.load &&
      filters.groups.state &&
      filters.brands.state &&
      filters.chains.state
    ) {
      setFilters((prev) => ({
        ...prev,
        state: true,
        load: true,
      }));
    }
  }, [
    filters.groups.state,
    filters.brands.state,
    filters.chains.state,
    filters.load,
  ]);

  // Checkbox and input changes
  const valueChanged = (event) => {
    const data = event.target;
    let filter = [];
    let brands = [];
    let chains = [];
    let obj = {};
    let id;

    switch (data.name) {
      case "groups":
        filter = filters.groups.list.map((option) =>
          option.grpKey === data.id
            ? { ...option, status: !option.status }
            : { ...option }
        );
        if (filter.some((group) => group.status)) {
          const selected = filter
            .filter((group) => group.status)
            .map((group) => group.grpKey);
          brands = filters.brands.orig.filter((brand) =>
            selected.includes(brand.grpKey)
          );
          chains = filters.chains.orig.filter((chain) =>
            selected.includes(chain.grpKey)
          );
        } else {
          brands = filters.brands.orig;
          chains = filters.chains.orig;
        }
        break;
      case "brands":
        filter = filters.brands.list.map((option) =>
          option.braKey === data.id
            ? { ...option, status: !option.status }
            : { ...option }
        );
        if (filter.some((brand) => brand.status)) {
          const selected = filter
            .filter((brand) => brand.status)
            .map((brand) => brand.braKey);
          chains = filters.chains.orig.filter((chain) =>
            selected.includes(chain.braKey)
          );
        } else {
          chains = filters.chains.orig;
        }
        break;
      case "chains":
        filter = filters.chains.list.map((option) =>
          option.chaKey === data.id
            ? { ...option, status: !option.status }
            : { ...option }
        );
        break;
      case "revCent":
        id = data.id.split(" | ")[0];
        filter = filters.revCent.list.map((option) =>
          option.id === parseInt(id)
            ? { ...option, status: !option.status }
            : { ...option }
        );
        break;
      case "payType":
        id = data.id.split(" | ")[0];
        filter = filters.payType.list.map((option) =>
          option.id === parseInt(id)
            ? { ...option, status: !option.status }
            : { ...option }
        );
        break;
      default:
        break;
    }

    if (brands.length) {
      obj.brands = {
        orig: filters.brands.orig,
        list: brands,
        state: true,
      };
    }
    if (chains.length) {
      obj.chains = {
        orig: filters.chains.orig,
        list: chains,
        state: true,
      };
    }

    setFilters((prev) => ({
      ...prev,
      [data.name]: {
        ...prev[data.name],
        list: filter,
      },
      ...obj,
    }));
  };

  // Date changes with validation
  const startDateChanged = (date) => {
    // Clear any existing errors
    setDateError("");

    // Validate the selected date
    const error = validateDateRange(date, filters.date.end.long);
    if (error) {
      setDateError(error);
      return;
    }

    let list = filters.date.list.map((l) => ({ ...l, status: false }));
    setFilters((prev) => ({
      ...prev,
      date: {
        ...prev.date,
        list,
        presetDays: null,
        start: {
          long: date,
          short: parseInt(date.getTime() / 1000),
        },
      },
    }));
  };

  const endDateChanged = (date) => {
    // Clear any existing errors
    setDateError("");

    const selectedDate = new Date(date);
    selectedDate.setHours(23, 59, 59, 59);

    // Validate the selected date
    const error = validateDateRange(filters.date.start.long, selectedDate);
    if (error) {
      setDateError(error);
      return;
    }

    let list = filters.date.list.map((l) => ({ ...l, status: false }));
    setFilters((prev) => ({
      ...prev,
      date: {
        ...prev.date,
        list,
        presetDays: null,
        end: {
          long: date,
          short: parseInt(selectedDate.getTime() / 1000),
        },
      },
    }));
  };

  // Date range buttons with validation
  const btnPress = (event) => {
    const data = event.target;
    let filter = {};
    let date = new Date();

    // Clear any existing errors
    setDateError("");

    switch (data.id) {
      case "date":
        filter.list = filters.date.list.map((d) =>
          d.value === parseInt(data.value)
            ? { ...d, status: !d.status }
            : { ...d, status: false }
        );
        let start = "";
        let end = "";
        switch (parseInt(data.value)) {
          case 0: // Yearly
            {
              let firstYear = new Date(date.getFullYear(), 0);
              firstYear.setHours(0, 0, 0, 0);
              let lastYear = new Date(date.getFullYear(), 12, 0);
              lastYear.setHours(23, 59, 59, 59);

              // Ensure year range is within 1 year limit
              if (firstYear < dateLimits.minDate) {
                firstYear = dateLimits.minDate;
              }

              start = firstYear.getTime();
              end = lastYear.getTime();
              filter.list[0].status = true;
            }
            break;
          case 1: // Monthly
            {
              let firstDayYear = new Date(
                date.getFullYear(),
                date.getMonth(),
                1,
                0
              );
              firstDayYear.setHours(0, 0, 0, 0);
              let lastDayYear = new Date(
                date.getFullYear(),
                date.getMonth() + 1,
                0
              );
              lastDayYear.setHours(23, 59, 59, 59);

              // Ensure month range is within 1 year limit
              if (firstDayYear < dateLimits.minDate) {
                firstDayYear = dateLimits.minDate;
              }

              start = firstDayYear.getTime();
              end = lastDayYear.getTime();
              filter.list[1].status = true;
            }
            break;
          case 2: // Weekly
            {
              let firstDayMonth = new Date(date);
              firstDayMonth.setDate(date.getDate() - 6);
              firstDayMonth.setHours(0, 0, 0, 0);
              let lastDayMonth = new Date();
              lastDayMonth.setHours(23, 59, 59, 59);

              // Ensure week range is within 1 year limit
              if (firstDayMonth < dateLimits.minDate) {
                firstDayMonth = dateLimits.minDate;
              }

              start = firstDayMonth.getTime();
              end = lastDayMonth.getTime();
              filter.list[2].status = true;
            }
            break;
          case 3: // Daily
            {
              let firstday = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0
              );
              firstday.setHours(0, 0, 0, 0);
              let lastday = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate() + 1,
                0
              );
              lastday.setHours(23, 59, 59, 59);
              start = firstday.getTime();
              end = lastday.getTime();
              filter.list[3].status = true;
            }
            break;
          default:
            break;
        }
        filter.start = {
          long: new Date(start),
          short: parseInt(start / 1000),
        };
        filter.end = {
          long: new Date(end),
          short: parseInt(end / 1000),
        };
        break;
      default:
        break;
    }

    setFilters((prev) => ({
      ...prev,
      [data.id]: {
        ...prev[data.id],
        ...filter,
      },
    }));
  };

  const selectPeriodPreset = (days) => {
    setDateError("");
    setFilters((prev) => ({
      ...prev,
      date: { ...prev.date, presetDays: days, ...getLastDaysRange(days) },
    }));
  };

  const selectCustomDateRange = () => {
    setDateError("");
    setFilters((prev) => ({
      ...prev,
      date: { ...prev.date, presetDays: null },
    }));
  };

  // Resize panel
  const handleResize = () => setReSize((prev) => !prev);

  // Clear all selections
  const clearAllSelections = () => {
    setFilters((prev) => ({
      ...prev,
      groups: {
        ...prev.groups,
        list: prev.groups.list.map((item) => ({ ...item, status: false })),
      },
      brands: {
        ...prev.brands,
        list: prev.brands.orig.map((item) => ({ ...item, status: false })),
      },
      chains: {
        ...prev.chains,
        list: prev.chains.orig.map((item) => ({ ...item, status: false })),
      },
      revCent: {
        ...prev.revCent,
        list: prev.revCent.orig.map((item) => ({ ...item, status: false })),
        availableList: prev.revCent.orig.map((item) => ({
          ...item,
          status: false,
        })),
      },
      payType: {
        ...prev.payType,
        list: prev.payType.list.map((item) => ({ ...item, status: false })),
      },
    }));
  };

  // Generate report with final validation
  const handleSetFilter = useCallback(() => {
    // Final validation before generating report
    const error = validateDateRange(
      filters.date.start.long,
      filters.date.end.long
    );
    if (error) {
      setDateError(error);
      return;
    }

    let group = filters.groups.orig;
    let brand = filters.brands.orig;
    let chain = filters.chains.orig;
    let revCent = filters.revCent.orig;
    let payType = filters.payType.orig;

    if (filters.groups.list.some((grp) => grp.status)) {
      group = filters.groups.list.filter((grp) => grp.status);
      brand = filters.brands.orig.filter((bra) =>
        group.find((grp) => grp.grpKey === bra.grpKey)
      );
      chain = filters.chains.orig.filter((cha) =>
        brand.find((bra) => bra.braKey === cha.braKey)
      );
      if (filters.brands.list.some((bra) => bra.status)) {
        brand = filters.brands.list.filter((bra) => bra.status);
      }
      if (filters.chains.list.some((cha) => cha.status)) {
        chain = filters.chains.list.filter((cha) => cha.status);
      }
    }

    if (
      !filters.groups.list.some((group) => group.status) &&
      filters.brands.list.some((brand) => brand.status)
    ) {
      brand = filters.brands.list.filter((brand) => brand.status);
      group = filters.groups.orig.filter((grp) =>
        brand.find((bra) => bra.grpKey === grp.grpKey)
      );
      chain = filters.chains.orig.filter((cha) =>
        brand.find((bra) => bra.braKey === cha.braKey)
      );
      if (filters.chains.list.some((chain) => chain.status)) {
        chain = filters.chains.list.filter((chain) => chain.status);
      }
    }

    if (
      !filters.groups.list.some((group) => group.status) &&
      !filters.brands.list.some((brand) => brand.status) &&
      filters.chains.list.some((chain) => chain.status)
    ) {
      chain = filters.chains.list.filter((cha) => cha.status);
      group = filters.groups.orig.filter((grp) =>
        chain.find((cha) => grp.grpKey === cha.grpKey)
      );
      brand = filters.brands.orig.filter((bra) =>
        chain.find((cha) => bra.braKey === cha.braKey)
      );
    }

    // Modified logic for revenue centers
    // Use the currently available revenue centers (the ones showing in Misc section)
    // instead of all original revenue centers
    if (filters.revCent.list.some((revCent) => revCent.status)) {
      // If specific revenue centers are selected, use only those
      revCent = filters.revCent.list.filter((revCent) => revCent.status);
    } else {
      // If no specific revenue centers are selected, use all currently available ones
      // This ensures we only use revenue centers that belong to the selected hierarchy
      revCent = filters.revCent.list.map((rc) => ({
        ...rc,
        status: true, // Mark all available ones as selected for the query
      }));
    }

    if (filters.payType.list.some((payType) => payType.status)) {
      payType = filters.payType.list.filter((payType) => payType.status);
    }

    const range =
      usesPeriodPresets && filters.date.presetDays != null
        ? getLastDaysRange(filters.date.presetDays)
        : filters.date;
    let date = {
      start: range.start.short,
      end: range.end.short,
    };

    const filterObject = {
      data: {
        groups: group,
        brands: brand,
        chains: chain,
        revCent: revCent,
        payType: payType,
        date: {
          start: date.start,
          end: date.end,
        },
      },
      base64: window.btoa(
        JSON.stringify({
          grpKey: { $in: group.map((group) => group.grpKey) },
          braKey: { $in: brand.map((brand) => brand.braKey) },
          chaKey: { $in: chain.map((chain) => chain.chaKey) },
          revCent: { $in: revCent.map((revCent) => revCent.name) },
          date: {
            $gt: date.start,
            $lt: date.end,
          },
        })
      ),
      timestamp: Date.now(),
    };

    props.setFilter(filterObject);
  }, [filters, props, usesPeriodPresets, validateDateRange]);

  // Count selected items
  const getSelectedCount = (list) => list.filter((item) => item.status).length;

  // Get filter status message for revenue centers
  // const getRevCenterFilterMessage = () => {
  //   const selectedGroups = filters.groups.list.filter((g) => g.status);
  //   const selectedBrands = filters.brands.list.filter((b) => b.status);
  //   const selectedChains = filters.chains.list.filter((c) => c.status);

  //   if (selectedChains.length > 0) {
  //     return `Showing revenue centers from ${selectedChains.length} selected outlet(s)`;
  //   } else if (selectedBrands.length > 0) {
  //     return `Showing revenue centers from ${selectedBrands.length} selected brand(s)`;
  //   } else if (selectedGroups.length > 0) {
  //     return `Showing revenue centers from ${selectedGroups.length} selected group(s)`;
  //   } else {
  //     return "Showing all available revenue centers";
  //   }
  // };

  return (
    <section className={["filter", reSize ? "hideWin" : ""].join(" ")}>
      <div className={[filters.state ? "" : "disabled"]} />
      <div className={["params", reSize ? "hide" : ""].join(" ")}>
        <div>
          <h2>
            <FontAwesomeIcon
              className={["icon"].join(" ")}
              icon={["fas", "layer-group"]}
            />
            Group
            {getSelectedCount(filters.groups.list) > 0 && (
              <span
                style={{
                  color: "#e27f06ff",
                  fontSize: "12px",
                  marginLeft: "5px",
                }}
              >
                ({getSelectedCount(filters.groups.list)} selected)
              </span>
            )}
          </h2>
          <div className={["paramBody"]}>
            <div className={["custom-control", "custom-checkbox"].join(" ")}>
              {filters.groups.list.length > 0 ? (
                filters.groups.list.map((data, key) => (
                  <div key={key}>
                    <input
                      type={"checkbox"}
                      className={["custom-control-input"]}
                      name="groups"
                      id={data.grpKey}
                      onChange={valueChanged}
                      checked={data.status}
                    />
                    <label
                      className={"custom-control-label-new"}
                      htmlFor={data.grpKey}
                    >
                      {data.name}
                    </label>
                  </div>
                ))
              ) : (
                <Skeleton width={"75%"} count={6} />
              )}
            </div>
          </div>
        </div>
        <div>
          <h2>
            <FontAwesomeIcon
              className={["icon"].join(" ")}
              icon={["fas", "store"]}
            />
            Brand
            {getSelectedCount(filters.brands.list) > 0 && (
              <span
                style={{
                  color: "#e27f06ff",
                  fontSize: "12px",
                  marginLeft: "5px",
                }}
              >
                ({getSelectedCount(filters.brands.list)} selected)
              </span>
            )}
          </h2>
          <div className={["paramBody"]}>
            <div className={["custom-control", "custom-checkbox"].join(" ")}>
              {filters.brands.list.length > 0 ? (
                filters.brands.list.map((data, key) => (
                  <div key={key}>
                    <input
                      type={"checkbox"}
                      className={["custom-control-input"]}
                      name="brands"
                      id={data.braKey}
                      onChange={valueChanged}
                      checked={data.status}
                    />
                    <label
                      className={"custom-control-label-new"}
                      htmlFor={data.braKey}
                    >
                      {data.name}
                    </label>
                  </div>
                ))
              ) : (
                <Skeleton width={"75%"} count={6} />
              )}
            </div>
          </div>
        </div>
        <div>
          <h2>
            <FontAwesomeIcon
              className={["icon"].join(" ")}
              icon={["fas", "tags"]}
            />
            Outlet
            {getSelectedCount(filters.chains.list) > 0 && (
              <span
                style={{
                  color: "#e27f06ff",
                  fontSize: "12px",
                  marginLeft: "5px",
                }}
              >
                ({getSelectedCount(filters.chains.list)} selected)
              </span>
            )}
          </h2>
          <div className={["paramBody"]}>
            <div className={["custom-control", "custom-checkbox"].join(" ")}>
              {filters.chains.list.length > 0 ? (
                filters.chains.list.map((data, key) => (
                  <div key={key}>
                    <input
                      type={"checkbox"}
                      className={["custom-control-input"]}
                      name="chains"
                      id={data.chaKey}
                      onChange={valueChanged}
                      checked={data.status}
                    />
                    <label
                      className={"custom-control-label-new"}
                      htmlFor={data.chaKey}
                    >
                      {data.name}
                    </label>
                  </div>
                ))
              ) : (
                <Skeleton width={"75%"} count={6} />
              )}
            </div>
          </div>
        </div>
        {!hideMisc && (
          <div>
            <h2>
              <FontAwesomeIcon
                className={["icon"].join(" ")}
                icon={["fas", "ellipsis-h"]}
              />
              Misc.
              {getSelectedCount(filters.revCent.list) > 0 && (
                <span
                  style={{
                    color: "#e27f06ff",
                    fontSize: "12px",
                    marginLeft: "5px",
                  }}
                >
                  ({getSelectedCount(filters.revCent.list)} selected)
                </span>
              )}
            </h2>
            {props.location !== "/app/sales/session" ? (
              <div className={["paramBody"]}>
                {/* --- for debugging purposes --- */}
                {/* <h1>
                Revenue Center
                {filters.revCent.list.length !==
                  filters.revCent.orig.length && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginLeft: "5px",
                    }}
                  >
                    (Filtered: {filters.revCent.list.length} of{" "}
                    {filters.revCent.orig.length})
                  </span>
                )}

              </h1> */}
                {/* <div
                style={{
                  fontSize: "11px",
                  color: "#666",
                  marginBottom: "10px",
                  fontStyle: "italic",
                }}
              > */}
                {/* {getRevCenterFilterMessage()} */}
                {/* </div> */}
                <div
                  className={["custom-control", "custom-checkbox"].join(" ")}
                >
                  {filters.revCent.list.length > 0 ? (
                    filters.revCent.list.map((data, key) => (
                      <div key={key}>
                        <input
                          type={"checkbox"}
                          className={["custom-control-input"]}
                          name="revCent"
                          id={`${data.id} | revCent`}
                          onChange={valueChanged}
                          checked={data.status}
                        />
                        <label
                          className={"custom-control-label-new"}
                          htmlFor={`${data.id} | revCent`}
                        >
                          {data.name}
                        </label>
                      </div>
                    ))
                  ) : (
                    <Skeleton width={"75%"} count={6} />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}
        {showDateFilter && (
          <div>
            <h2>
              <FontAwesomeIcon
                className={["icon"].join(" ")}
                icon={["fas", "calendar-alt"]}
              />
              Date
            </h2>
            <div className={["paramBody"]}>
              {usesPeriodPresets ? (
                <div className={["paramContainer"]}>
                  <div
                    className={["options", "periodPresets"].join(" ")}
                    role="radiogroup"
                    aria-label="Report period"
                  >
                    {MENU_PERIOD_PRESETS.map(({ label, days }) => (
                      <button
                        key={days}
                        type="button"
                        role="radio"
                        aria-checked={filters.date.presetDays === days}
                        className={
                          filters.date.presetDays === days ? "selected" : ""
                        }
                        onClick={() => selectPeriodPreset(days)}
                      >
                        {label}
                      </button>
                    ))}
                    {/* <button
                    type="button"
                    role="radio"
                    aria-checked={filters.date.presetDays === null}
                    className={
                      filters.date.presetDays === null ? "selected" : ""
                    }
                    onClick={selectCustomDateRange}
                  >
                    Custom range
                  </button> */}
                  </div>
                  <p>
                    {filters.date.start.long.toLocaleDateString()} –{" "}
                    {filters.date.end.long.toLocaleDateString()}
                  </p>
                  {filters.date.presetDays === null && (
                    <>
                      <div className={"dateRange"}>
                        <div>
                          <p>Start</p>
                          <DatePicker
                            selected={filters.date.start.long}
                            onChange={startDateChanged}
                            name={"date"}
                            id={"start"}
                            todayButton={"Today"}
                            dateFormat="dd/MM/yyyy"
                            {...(!isAdmin && {
                              minDate: dateLimits.minDate,
                              maxDate: dateLimits.maxDate,
                            })}
                          />
                        </div>
                        <div>
                          <p>End</p>
                          <DatePicker
                            selected={filters.date.end.long}
                            onChange={endDateChanged}
                            name={"date"}
                            todayButton={"Today"}
                            dateFormat="dd/MM/yyyy"
                            {...(!isAdmin && {
                              minDate: Math.max(
                                filters.date.start.long,
                                dateLimits.minDate
                              ),
                              maxDate: dateLimits.maxDate,
                            })}
                            id={"end"}
                          />
                        </div>
                      </div>
                      {dateError && <p className={"dateError"}>{dateError}</p>}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className={["paramContainer"]}>
                    <p>Date Range</p>
                    {dateError && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginBottom: "10px",
                          padding: "5px",
                          backgroundColor: "#f8d7da",
                          borderRadius: "4px",
                          border: "1px solid #f5c6cb",
                        }}
                      >
                        {dateError}
                      </div>
                    )}
                    <div className={["dateRange"]}>
                      <div>
                        <p>Start</p>
                        <DatePicker
                          selected={filters.date.start.long}
                          onChange={startDateChanged}
                          name={"date"}
                          id={"start"}
                          todayButton={"Today"}
                          dateFormat="dd/MM/yyyy"
                          {...(!isAdmin && {
                            minDate: dateLimits.minDate,
                            maxDate: dateLimits.maxDate,
                          })}
                        />
                      </div>
                      <div>
                        <p>End</p>
                        <DatePicker
                          selected={filters.date.end.long}
                          onChange={endDateChanged}
                          name={"date"}
                          todayButton={"Today"}
                          dateFormat="dd/MM/yyyy"
                          {...(!isAdmin && {
                            minDate: Math.max(
                              filters.date.start.long,
                              dateLimits.minDate
                            ),
                            maxDate: dateLimits.maxDate,
                          })}
                          id={"end"}
                        />
                      </div>
                    </div>
                    <p>Set by date</p>
                  </div>
                  <div className={["options"]}>
                    {filters.date.list.map((data, key) => (
                      <button
                        key={key}
                        className={[data.status ? "selected" : ""].join(" ")}
                        onClick={btnPress}
                        value={data.value}
                        id={"date"}
                        type="button"
                      >
                        {data.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={["footer"]}>
        <div className={["footerHead"]}>
          <button onClick={handleResize}>
            <FontAwesomeIcon
              className={["icon"].join(" ")}
              icon={["fas", reSize ? "plus-square" : "minus-square"]}
            />
          </button>
          <h2>Insights Report</h2>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className={[reSize ? "hide" : ""]}
            onClick={clearAllSelections}
          >
            Clear Filter
          </button>
          <button
            className={[reSize ? "hide" : ""]}
            onClick={handleSetFilter}
            disabled={!filters.state || !!dateError}
          >
            Generate Report
          </button>
        </div>
      </div>
    </section>
  );
}
export default Filter;
